import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { LoginPayload, login as loginRequest, loginWithGoogle as loginWithGoogleRequest, logout as logoutRequest } from '../services/authApi';
import { clearStoredTokens, getStoredAccessToken } from '../services/apiClient';
import { fetchProfile } from '../services/platformApi';
import { UserProfile } from '../types/UserProfile';

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  profile: UserProfile | null;
  login: (payload: LoginPayload) => Promise<void>;
  loginWithGoogle: (idToken: string, tenantSlug?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);

  const refreshProfile = useCallback(async () => {
    const data = await fetchProfile();
    setProfile(data);
  }, []);

  useEffect(() => {
    async function bootstrap() {
      if (!getStoredAccessToken()) {
        setIsLoading(false);
        return;
      }

      try {
        await refreshProfile();
      } catch {
        clearStoredTokens();
        setProfile(null);
      } finally {
        setIsLoading(false);
      }
    }

    void bootstrap();
  }, [refreshProfile]);

  const login = useCallback(async (payload: LoginPayload) => {
    await loginRequest(payload);
    try {
      await refreshProfile();
      toast.success('Login realizado com sucesso.');
    } catch {
      clearStoredTokens();
      setProfile(null);
      throw new Error('profile_fetch_failed');
    }
  }, [refreshProfile]);

  const loginWithGoogle = useCallback(async (idToken: string, tenantSlug?: string) => {
    await loginWithGoogleRequest(idToken, tenantSlug);
    try {
      await refreshProfile();
      toast.success('Login realizado com sucesso.');
    } catch {
      clearStoredTokens();
      setProfile(null);
      throw new Error('profile_fetch_failed');
    }
  }, [refreshProfile]);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      setProfile(null);
      toast.info('Sessão encerrada.');
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    isAuthenticated: !!getStoredAccessToken() && !!profile,
    isLoading,
    profile,
    login,
    loginWithGoogle,
    logout,
    refreshProfile,
  }), [isLoading, profile, login, loginWithGoogle, logout, refreshProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider.');
  }
  return context;
}
